import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { Table, TableCell, TableRow, TablePagination, TableFooter, TableContainer } from '@material-ui/core';
import { addGoalsToGlobalGoalsMap } from 'actions/globalGoalsMapActions';
import { openObjectiveDetailsPanelActionCreator } from 'actions/objectiveDetailsPanelActions';
import GoalProgressBar from 'components/GoalProgressBar';
import MeshTable from 'components/TableComponents/MeshTable';
import { StyledAvatar } from 'components/avatars';
import TextParseComponent from 'components/feed/TextParseComponent';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import Task1 from 'models/task1';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { StyledNodesView } from 'screens/common/sidepanel/sidepanelStyles';
import { selectGoalFromGlobalGoals } from 'selectors/globalGoalSelector';
import styled from 'styled-components';
import { StyledTypography, StyledBox, StyledGrid, PaddingCSS, StyledBorderedPaper, FlexContainer } from 'styles/common';

import { convertDateToString4 } from 'constants/helper';
import { KrSvgIcon, ObjectiveSvgIcon, PrivateKRSvgIcon } from 'constants/icons';
import {
  GOAL_ANALYTICS_EVENTS,
  sendGoalAnalyticsEvents,
  sendOpenedObjectivePanelEvent,
} from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';
import theme from 'constants/theme';

import { fetchGoals, fetchGoalsCount } from 'services/analytics/goal';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { showTaskDetails } from 'services/taskDetails';

import { ErrorMessage, Loader, EmptyBar } from '../commonComponents';
import { useGoalAnalytics } from './GoalAnalyticsContext';
import NoData from './GoalObjective/components/NoData';

const ERROR_MSG = 'Some error occured while fetching goals.';

const columnList = [
  {
    id: 'TEXT',
    title: 'Goals',
    fixedWidth: '35rem',
    justify: 'flex-start',
    tooltip: '',
    component: (goal) => {
      const isStrikedThroughStatus =
        (goal?.goalClosureStatus === 'DROPPED' ||
          goal?.goalClosureStatus === 'ACHIEVED' ||
          goal?.goalClosureStatus === 'NOT_ACHIEVED') &&
        goal?.status === 'COMPLETED';
      return (
        <StyledTableCell>
          <FlexContainer style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {goal?.visibilityType === 'PRIVATE' ? (
              <PrivateKRSvgIcon color={theme.palette.icons.krBlue} size="fixed18px" rightGutter={1} />
            ) : (
              <KrSvgIcon color={theme.palette.icons.krBlue} size="fixed18px" rightGutter={1} />
            )}
            <StyledTypography wordBreak variant="body2" textDecorationColor="textPrimary">
              <TextParseComponent
                text={goal.text}
                res={goal.macros}
                isStrikedThrough={isStrikedThroughStatus}
                isGreyedOut={goal?.goalClosureStatus === 'DROPPED'}
              />
            </StyledTypography>
          </FlexContainer>
        </StyledTableCell>
      );
    },
  },
  {
    id: 'TAGS',
    title: 'Objective',
    fixedWidth: '16rem',
    justify: 'left',
    tooltip: '',
    component: (goal, handleObjectiveNameClick) => {
      const handleClick = (ev) => {
        if (goal?.subObjective?.objectiveTimeBoundId) {
          ev.stopPropagation();
          handleObjectiveNameClick(goal?.subObjective?.objectiveTimeBoundId);
        }
      };

      return (
        <StyledTableCell borderLeft align="left" onClick={handleClick}>
          {goal.subObjective ? (
            <FlexContainer style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <ObjectiveSvgIcon color="#7E5BC0" size="fixed20px" style={{ flex: '0 0 auto' }} rightGutter={1} />
              <StyledTypography variant="body2" color={theme.palette.text.heading} style={{ flex: '1 1 auto' }}>
                {goal.subObjective.displayName}
              </StyledTypography>
            </FlexContainer>
          ) : (
            <EmptyBar />
          )}
        </StyledTableCell>
      );
    },
  },
  {
    id: 'LAST_CHECKIN_TS',
    title: 'Last Check-in',
    fixedWidth: '10rem',
    justify: 'center',
    tooltip: '',
    component: (goal) => (
      <StyledTableCell borderLeft align="center">
        <StyledGrid container justify="center">
          {goal.lastCheckInTS ? (
            <StyledBox padding="0.5rem 3ch" bgcolor={theme.palette.background.grey} borderRadius={4}>
              <StyledTypography variant="body2" color={theme.palette.text.heading} weight={600}>
                {goal.lastCheckInTS && convertDateToString4(goal.lastCheckInTS)}
              </StyledTypography>
            </StyledBox>
          ) : (
            <EmptyBar />
          )}
        </StyledGrid>
      </StyledTableCell>
    ),
  },
  {
    id: 'NAME',
    title: 'Owners',
    fixedWidth: '12rem',
    justify: 'center',
    tooltip: '',
    component: (goal) => (
      <StyledTableCell borderLeft paddingLeft="1rem !important" align="center">
        {goal.toIds && goal.toIds.length > 0 ? (
          <StyledNodesView
            employees={goal.toIds}
            maxNodes={4}
            useEmployeeClick={false}
            renderAvatar={(props) => (
              <StyledAvatar
                {...props}
                size="smallest"
                leftGutter={-1}
                border={
                  props?.ownerType === Task1.EMPLOYEE_TYPES.owner
                    ? `2px solid ${theme.palette.green.main}`
                    : '2px solid #EAEAEA'
                }
              />
            )}
            onClick={(e) => {
              e.preventDefault();
            }}
          />
        ) : (
          <EmptyBar />
        )}
      </StyledTableCell>
    ),
  },
  {
    id: 'GOAL_PROGRESS_PERCENTAGE',
    title: 'Progress',
    fixedWidth: '16rem',
    justify: 'center',
    tooltip: '',
    component: (goal) => (
      <StyledTableCell borderLeft align="center">
        <GoalProgressBar goal={goal} />
        <StyledTypography variant="body2" display="inline">
          {`${Number(goal.getGoalProgress(0) || 0)}%`}
        </StyledTypography>
      </StyledTableCell>
    ),
  },
];

// this needs to support pagination, sorting etc

const GoalRow = ({ goal: localGoal, handleObjectiveNameClick, columnList, onClick }) => {
  const globalGoal = useSelector(selectGoalFromGlobalGoals(localGoal?.id)) ?? localGoal;

  return (
    <StyledTableRow onClick={onClick}>
      {columnList.map((col) => col.component(globalGoal, handleObjectiveNameClick))}
    </StyledTableRow>
  );
};

/**
 * @deprecated 06-08-2024
 */

const GoalsList = ({ tagId = null, employeeId = null }) => {
  const history = useHistory();

  const [goals, setGoals] = useState(null);

  const [loading, setLoading] = useState(false);

  const [orderBy, setOrderBy] = useState({ columnId: null, orderType: 'ASC' });

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  // pagination helpers
  const [currentPage, setCurrentPage] = useState(0);
  const [count, setCount] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const {
    state: { currentState },
    setNoData,
    setErrorData,
  } = useGoalAnalytics();
  const { selectedCohort, selectedTimeBoundPeriods } = currentState;

  const dispatch = useDispatch();

  const fetchData = () => {
    setLoading(true);

    fetchGoals({
      limit: rowsPerPage,
      offset: currentPage * rowsPerPage,
      cohort: selectedCohort,
      timeBounds: selectedTimeBoundPeriods,
      tagId,
      employeeId,
      orderByWithFilterTagType: orderBy,
      isPrivateGoalRequired: true,
    })
      .then((goals) => {
        setGoals(goals);
        if (goals.length === 0) {
          setNoData(true);
        }
        dispatch(addGoalsToGlobalGoalsMap({ goals }));
      })
      .catch((err) => {
        console.log(err);
        setErrorData(true);
        dispatch(showErrorSnackbar('Failed to fetch goals!'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPage(0);
    fetchData();
  }, [rowsPerPage]);

  useEffect(() => {
    setLoading(true);
    fetchGoalsCount({
      cohort: selectedCohort,
      timeBounds: selectedTimeBoundPeriods,
      tagId,
      employeeId,
    })
      .then((res) => {
        setCount(res.entity.count);
        fetchData();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Failed to fetch goals!'));
        console.error(err);
        setLoading(false);
      });
  }, [currentPage, selectedCohort, selectedTimeBoundPeriods]);

  const showDetails = useCallback((index, taskId, type) => {
    sendGoalAnalyticsEvents({
      eventName: GOAL_ANALYTICS_EVENTS.openedGoalSidePanel,
      page: {
        pageName: routesForTracking.analyticsGoals.pageName,
        flow: 'Activity',
      },
      groupBy: 'Individual',
      goalId: taskId,
    });
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: null,
        employee: {},
        // TODO: remove onUpdate and handle goal changes in GoalRow.
        onUpdate: (goal) => {
          if (goal) {
            setGoals((prevData) => [...prevData.slice(0, index), goal, ...prevData.slice(index + 1)]);
          }
        },
        type,
      })
    );
  }, []);

  useEffect(() => {
    if (orderBy.columnId !== null) fetchData();
  }, [orderBy]);

  const handleSort = useCallback(
    (id, orderBy) => {
      if (orderBy.columnId === id) {
        setOrderBy((prev) => ({
          ...prev,
          orderType: prev.orderType === 'ASC' ? 'DESC' : 'ASC',
        }));
      } else {
        setOrderBy({
          columnId: id,
          filterTagType: id,
          orderType: 'ASC',
        });
      }
    },
    [setOrderBy]
  );

  const handleObjectiveNameClick = useCallback((objectiveTimeBoundId) => {
    dispatch(
      openObjectiveDetailsPanelActionCreator({
        objectiveTimeBoundId,
        onNameChange: () => {
          dispatch(showSuccessSnackbar(`${objectivesName} renamed! Please refresh.`));
        },
        onDelete: () => {
          dispatch(showSuccessSnackbar(`${objectivesName} deleted! Please refresh.`));
        },
        onLevelChange: () => {
          // dispatch(showInfoSnackbar('Objective Level changed'));
        },
        onReAlign: () => {
          dispatch(showSuccessSnackbar(`${objectivesName} realigned! Please refresh.`));
        },
      })
    );

    sendOpenedObjectivePanelEvent({
      eventProperties: {
        source: 'Insights Page List',
      },
    });
  }, []);

  const mappedHeaders = useMemo(
    () =>
      columnList.map((col) => ({
        ...col,
        isSortable: true,
        orderBy,
        handleSort,
      })),
    [orderBy, handleSort]
  );

  const dataRows = useMemo(() => {
    if (!goals) return [];
    return goals.map((goal, index) => (
      <GoalRow
        key={goal.id}
        goal={goal}
        onClick={() => showDetails(index, goal.id, goal.type)}
        columnList={columnList}
        handleObjectiveNameClick={handleObjectiveNameClick}
      />
    ));
  }, [goals, columnList, showDetails, handleObjectiveNameClick]);

  const tableFooter = useMemo(
    () => (
      <TableFooter>
        <StyledTableRow>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15]}
            colSpan={columnList && columnList.length}
            count={count}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
            }}
            onChangePage={(_, pageNum) => setCurrentPage(pageNum)}
            onChangeRowsPerPage={({ target }) => setRowsPerPage(target.value)}
          />
        </StyledTableRow>
      </TableFooter>
    ),
    [columnList, rowsPerPage, count, currentPage, setCurrentPage, setRowsPerPage]
  );

  const showLoader = () => <Loader />;

  const showNoGoalsMessage = () => <NoData />;

  const showErrorMessage = () => <ErrorMessage errorText={ERROR_MSG} />;

  // useEffect( () => {
  //   fetchData();
  // },[]);

  return (
    <>
      {loading ? (
        showLoader()
      ) : goals === null ? (
        showErrorMessage()
      ) : goals.length === 0 ? (
        showNoGoalsMessage()
      ) : (
        <StyledBorderedPaper overFlow="auto" marginBottom="2rem" padding="0" topGutter={2}>
          <StyledTableContainer>
            <MeshTable headers={mappedHeaders} dataRows={dataRows} tableFooter={tableFooter} />
          </StyledTableContainer>
        </StyledBorderedPaper>
      )}
    </>
  );
};

const StyledTable = styled(Table)`
  margin-top: 0.5rem;
  th,
  td {
    padding: 8px 12px 8px 12px;
  }
  tr {
    td,
    th {
      &:first-child {
        padding-left: 1.5rem;
      }
    }
  }

  tfoot {
    td {
      padding: 0;
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  border-left: ${(props) => props.borderLeft && `1px solid ${props.theme.palette.borders.lightGrey}`};
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'inherit')};
  ${PaddingCSS};

  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
`;

const StyledTableRow = styled(TableRow)`
  & * {
    cursor: pointer;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;

export default GoalsList;
